const PHONE_VISIBLE_PREFIX_LENGTH = 2;
const PHONE_INTL_VISIBLE_PREFIX_LENGTH = 4;
const PHONE_VISIBLE_SUFFIX_LENGTH = 3;
const PHONE_INTL_PREFIX = '+';
const LOCAL_PART_DOT_INDEX = 1;
const LOCAL_PART_DOT_PREFIX_LENGTH = 2;
const DOMAIN_MIN_LABEL_COUNT = 2;
const DOMAIN_COMPOUND_TLD_LABEL_COUNT = 2;
const DOMAIN_SINGLE_TLD_LABEL_COUNT = 1;
const DOMAIN_COUNTRY_CODE_LENGTH = 2;
const DOMAIN_SECOND_LEVEL_SHORT_MAX_LENGTH = 3;
const LAST_LABEL_OFFSET = 1;
const SECOND_LAST_LABEL_OFFSET = 2;

const redactPhone = (phone: string): string => {
  const normalizedPhone = phone.split(' ').join('');
  const visiblePrefixLength = normalizedPhone.startsWith(PHONE_INTL_PREFIX)
    ? PHONE_INTL_VISIBLE_PREFIX_LENGTH
    : PHONE_VISIBLE_PREFIX_LENGTH;
  const minRedactableLength = visiblePrefixLength + PHONE_VISIBLE_SUFFIX_LENGTH;
  const hasEnoughCharsForSuffix = normalizedPhone.length > minRedactableLength;
  const visibleSuffixLength = hasEnoughCharsForSuffix ? PHONE_VISIBLE_SUFFIX_LENGTH : 0;

  const visiblePrefix = normalizedPhone.slice(0, visiblePrefixLength);
  const visibleSuffix = visibleSuffixLength === 0 ? '' : normalizedPhone.slice(-visibleSuffixLength);
  const redactionLength = Math.max(0, normalizedPhone.length - (visiblePrefixLength + visibleSuffixLength));

  return `${visiblePrefix}${'*'.repeat(redactionLength)}${visibleSuffix}`;
};

const redactEmailLocalPart = (localPart: string): string => {
  const preserveDotAfterFirstChar = localPart[LOCAL_PART_DOT_INDEX] === '.';
  const preservedPrefix = preserveDotAfterFirstChar ? localPart.slice(0, LOCAL_PART_DOT_PREFIX_LENGTH) : localPart[0];
  const remaining = localPart.slice(preservedPrefix.length);
  const redactedCharCount = [...remaining].filter((char) => char !== '.').length;

  return `${preservedPrefix}${'*'.repeat(redactedCharCount)}`;
};

const redactEmailDomain = (domain: string): string => {
  const labels = domain.split('.');
  if (labels.length < DOMAIN_MIN_LABEL_COUNT) return domain;

  const lastLabelIndex = labels.length - LAST_LABEL_OFFSET;
  const secondLastLabelIndex = labels.length - SECOND_LAST_LABEL_OFFSET;
  const lastLabel = labels[lastLabelIndex];
  const secondLastLabel = labels[secondLastLabelIndex];
  const hasCompoundTld =
    lastLabel.length === DOMAIN_COUNTRY_CODE_LENGTH &&
    secondLastLabel.length <= DOMAIN_SECOND_LEVEL_SHORT_MAX_LENGTH &&
    labels.length > DOMAIN_MIN_LABEL_COUNT;

  const preservedTldLabelCount = hasCompoundTld ? DOMAIN_COMPOUND_TLD_LABEL_COUNT : DOMAIN_SINGLE_TLD_LABEL_COUNT;
  const tldStartIndex = labels.length - preservedTldLabelCount;
  const redactedLabels = labels.map((label, index) => {
    if (index >= tldStartIndex) return label;
    if (!label) return label;
    return `${label[0]}${'*'.repeat(Math.max(0, label.length - 1))}`;
  });

  return redactedLabels.join('.');
};

const redactEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;

  return `${redactEmailLocalPart(localPart)}@${redactEmailDomain(domain)}`;
};

export { redactEmail, redactPhone };
